import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '../utils/api';
import { useSiteContent } from '../hooks/useSiteContent';

interface Subject {
  id: string;
  name: string;
  category: 'ELEVEN_PLUS' | 'GCSE' | 'A_LEVEL' | 'UNIVERSITY';
  onlineRate: number;
  inPersonRate: number;
  isActive: boolean;
}

interface HourPackageConfig {
  id: string;
  packageType: string;
  hours: number;
  discountPercent: number;
  isActive?: boolean;
}

type LevelKey = 'ELEVEN_PLUS' | 'GCSE' | 'A_LEVEL' | 'UNIVERSITY';

interface LevelDef {
  key: LevelKey;
  eyebrow: string;
  heading: string;
  introKey: string;
  introFallback: string;
}

const LEVELS: LevelDef[] = [
  {
    key: 'ELEVEN_PLUS',
    eyebrow: '11+',
    heading: '11+',
    introKey: 'subjects_11plus_intro',
    introFallback:
      'For Years 4–6 preparing for grammar school or independent school entry. I help children build exam technique without killing their interest in the subject.',
  },
  {
    key: 'GCSE',
    eyebrow: 'GCSE',
    heading: 'GCSE',
    introKey: 'subjects_gcse_intro',
    introFallback:
      'For Years 9–11. I work on exam technique, question spotting, and the specific habits that separate a grade 7 from a grade 9.',
  },
  {
    key: 'A_LEVEL',
    eyebrow: 'A-level',
    heading: 'A-level',
    introKey: 'subjects_alevel_intro',
    introFallback:
      "For Years 12–13. I know these specifications well because I'm sitting them now. I'll tell you what actually comes up and how examiners think.",
  },
  {
    key: 'UNIVERSITY',
    eyebrow: 'Oxbridge',
    heading: 'Oxbridge',
    introKey: 'subjects_oxbridge_intro',
    introFallback:
      'For Year 12 and Year 13 applicants to Oxford, Cambridge and other highly competitive universities. See the dedicated Oxbridge page for details.',
  },
];

const formatRate = (rate: number): string =>
  Number.isFinite(rate) && rate > 0 ? `£${rate}/hr` : '—';

interface DisplaySubject {
  name: string;
  onlineRate?: number;
  inPersonRate?: number;
}

const SubjectsPage: React.FC = () => {
  const c = useSiteContent();
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [packages, setPackages] = useState<HourPackageConfig[]>([]);
  const [packagesAvailable, setPackagesAvailable] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/subjects');
        if (cancelled) return;
        setSubjects(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!cancelled) setSubjects([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/hour-packages/configs/public');
        if (cancelled) return;
        const data = Array.isArray(res.data) ? res.data : [];
        const active = data.filter((p: HourPackageConfig) => p.isActive !== false);
        if (active.length > 0) {
          setPackages(active.sort((a: HourPackageConfig, b: HourPackageConfig) => a.hours - b.hours));
          setPackagesAvailable(true);
        } else {
          setPackagesAvailable(false);
        }
      } catch {
        if (!cancelled) setPackagesAvailable(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const subjectsFor = (level: LevelKey): DisplaySubject[] => {
    const list: DisplaySubject[] = subjects
      .filter((s) => s.category === level && s.isActive !== false)
      .map((s) => ({ name: s.name, onlineRate: s.onlineRate, inPersonRate: s.inPersonRate }));

    if (level === 'UNIVERSITY') {
      const fallback = ['TARA', 'Interview preparation'];
      for (const f of fallback) {
        if (!list.some((d) => d.name.toLowerCase() === f.toLowerCase())) {
          list.push({ name: f });
        }
      }
    }

    return list;
  };

  return (
    <main className="bg-cream">
      <Helmet>
        <title>Subjects and rates — EJL Education</title>
        <meta name="description" content="11+, GCSE, A-level and Oxbridge subjects with online and in-person rates. Maths, English, Economics, Philosophy, Politics, and more." />
      </Helmet>
      <section className="section">
        <div className="container-content">
          <h1 className="mb-4">{c('subjects_heading', 'Subjects and rates')}</h1>
          <p className="text-muted max-w-2xl text-body">
            {c(
              'subjects_subhead',
              'Grouped by level, with online and in-person hourly rates. All sessions are one-to-one. The 20-minute intro call is £5.'
            )}
          </p>
        </div>
      </section>

      {LEVELS.map((level, idx) => {
        const items = subjectsFor(level.key);
        return (
          <section
            key={level.key}
            className={`${idx === 0 ? '' : 'border-t border-rule'} py-16 md:py-20`}
          >
            <div className="container-content">
              <p className="eyebrow mb-4">{level.eyebrow}</p>
              <h2 className="mb-6">{level.heading}</h2>
              <p className="text-muted max-w-2xl text-body mb-10">
                {c(level.introKey, level.introFallback)}
              </p>

              {items.length === 0 ? (
                <p className="text-muted italic">(subjects coming soon)</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-rule">
                        <th className="py-3 pr-6 eyebrow font-sans font-medium text-ochre">Subject</th>
                        <th className="py-3 px-6 eyebrow font-sans font-medium text-ochre w-[22%] sm:w-[18%]">Online</th>
                        <th className="py-3 pl-6 eyebrow font-sans font-medium text-ochre w-[22%] sm:w-[18%]">In person</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, i) => (
                        <tr key={`${level.key}-${item.name}-${i}`} className="border-b border-rule">
                          <td className="py-3 pr-6 text-body font-serif text-ink">{item.name}</td>
                          <td className="py-3 px-6 text-body font-serif text-ink">
                            {formatRate(item.onlineRate ?? NaN)}
                          </td>
                          <td className="py-3 pl-6 text-body font-serif text-ink">
                            {formatRate(item.inPersonRate ?? NaN)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>
        );
      })}

      {packagesAvailable && packages.length > 0 && (
        <section className="border-t border-rule py-16 md:py-20">
          <div className="container-content">
            <h2 className="mb-4">{c('packages_heading', 'Hour packages')}</h2>
            <p className="text-muted max-w-2xl text-body mb-8">
              {c(
                'packages_body',
                'Prepaid packages at a slight discount. Useful for students committing to weekly sessions across a term.'
              )}
            </p>

            <ul className="max-w-xl">
              {packages.map((pkg) => (
                <li
                  key={pkg.id}
                  className="flex items-baseline justify-between border-t border-rule py-4 text-body font-serif text-ink last:border-b"
                >
                  <span>{pkg.hours} hours</span>
                  <span className="text-ochre">{pkg.discountPercent}% off</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <section className="border-t border-rule py-16 md:py-20">
        <div className="container-content">
          <ul className="max-w-2xl">
            <li className="border-t border-rule py-4 text-body text-ink">
              {c('rates_note_1', 'No minimum commitment for pay-as-you-go.')}
            </li>
            <li className="border-t border-rule py-4 text-body text-ink">
              {c('rates_note_2', 'In-person sessions within London Zones 1–4.')}
            </li>
            <li className="border-t border-b border-rule py-4 text-body text-ink">
              {c('rates_note_3', "Invoiced monthly. Cancel any session with 24 hours' notice.")}
            </li>
          </ul>
          <p className="mt-6 text-muted text-body">
            Sessions are 1 hour or 1.5 hours. Prices scale proportionally.
          </p>
        </div>
      </section>

      <section className="border-t border-rule py-16 md:py-20">
        <div className="container-content">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <p className="text-body text-ink max-w-2xl">
              Not sure which level fits? Book a £5 20-minute intro call and we'll figure it out.
            </p>
            <Link to="/contact" className="btn btn-primary self-start md:self-auto">
              Book £5 intro call
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default SubjectsPage;
